import { Component } from '@theme/component';
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu]
 *
 * @extends {Component}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Timer used to detect when pointer movement stops.
   * @type {number | undefined}
   */
  #pointerIdleTimer;

  /**
   * Timeout used to clean up the submenu mutation observer.
   * @type {number | undefined}
   */
  #observerCleanupTimer;

  /**
   * Last known pointer position.
   * @type {Object}
   */
  #lastPointer = {
    x: 0,
    y: 0,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener('resize', this.#resizeListener);

    /*
     * pointerover bubbles, so this catches the first menu item when the
     * pointer enters the header directly from outside the header.
     */
    this.addEventListener(
      'pointerover',
      this.#onMenuPointerOver,
      true
    );

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    setHeaderMenuStyle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);

    this.removeEventListener(
      'pointerover',
      this.#onMenuPointerOver,
      true
    );

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    clearTimeout(this.#observerCleanupTimer);
    this.#observerCleanupTimer = undefined;

    this.#cleanupMutationObserver();
  }

  /**
   * Recalculate the header menu style after resizing.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Close the menu when the pointer leaves the overflow submenu.
   */
  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Fallback activation handler.
   *
   * This fixes the case where the pointer enters the header from outside
   * and lands directly on a menu item, but the original pointerenter
   * activation does not run reliably.
   *
   * @param {PointerEvent} event
   */
  #onMenuPointerOver = (event) => {
    if (
      event.pointerType &&
      event.pointerType !== 'mouse'
    ) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest(
      '.menu-list__list-item'
    );

    if (
      !(listItem instanceof HTMLElement) ||
      !this.contains(listItem)
    ) {
      return;
    }

    /*
     * Ignore pointerover events caused by moving between child elements
     * inside the same list item.
     */
    if (
      event.relatedTarget instanceof Node &&
      listItem.contains(event.relatedTarget)
    ) {
      return;
    }

    const item = findMenuItem(listItem);

    if (!item || item === this.#state.activeItem) {
      return;
    }

    this.#activateFromElement(listItem);
  };

  /**
   * Update the safety-box idle state.
   *
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeItem.dataset.safetyBox = String(moving);

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        const currentActiveItem =
          this.#state.activeItem;

        if (!currentActiveItem) return;

        currentActiveItem.dataset.safetyBox = 'false';
        this.#reconcilePointerTarget();
      }, 75);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Recheck the element under the pointer after the safety box changes.
   */
  #reconcilePointerTarget() {
    const x = this.#lastPointer.x;
    const y = this.#lastPointer.y;

    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);

      if (!(target instanceof Element)) return;

      const listItem = target.closest(
        '.menu-list__list-item'
      );

      if (
        !(listItem instanceof HTMLElement) ||
        !this.contains(listItem)
      ) {
        return;
      }

      const item = findMenuItem(listItem);

      if (
        !item ||
        item === this.#state.activeItem
      ) {
        return;
      }

      this.#activateFromElement(listItem);
    });
  }

  /**
   * Begin pointer tracking for the active menu item.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove
      );
    }

    const itemRect = item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent?.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const boundary = isOverlap
      ? this.headerComponent?.querySelector(
          '.header__row--top'
        )
      : this.headerComponent;

    const boundaryBottom =
      boundary?.getBoundingClientRect().bottom ?? 0;

    const boxHeight = Math.max(
      0,
      boundaryBottom - itemRect.top
    );

    item.style.setProperty(
      '--box-height',
      `${boxHeight}px`
    );
  }

  /**
   * Stop pointer tracking for an item.
   *
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;

    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow submenu element.
   *
   * @returns {HTMLElement | null}
   */
  get overflowMenu() {
    const overflow =
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      );

    return overflow instanceof HTMLElement
      ? overflow
      : null;
  }

  /**
   * Whether the overflow list is currently hovered.
   *
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  /**
   * Get the parent header component.
   *
   * @returns {HTMLElement | null}
   */
  get headerComponent() {
    const header = this.closest('header-component');

    return header instanceof HTMLElement
      ? header
      : null;
  }

  /**
   * Activation entry point used by Horizon event bindings.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const sourceElement =
      event.target.closest('.menu-list__list-item') ??
      event.target;

    this.#activateFromElement(sourceElement);
  };

  /**
   * Activate the menu item associated with a specific element.
   *
   * Separating this from activate() allows the delegated pointerover
   * fallback to activate the correct list item reliably.
   *
   * @param {Element} sourceElement
   */
  #activateFromElement(sourceElement) {
    const headerComponent = this.headerComponent;

    if (!headerComponent) return;

    const item = findMenuItem(sourceElement);

    if (!item || item === this.#state.activeItem) {
      return;
    }

    const listItem = sourceElement.closest(
      '.menu-list__list-item'
    );

    const sourceSlot =
      listItem?.getAttribute('slot') ??
      sourceElement.getAttribute('slot') ??
      '';

    const isDefaultSlot =
      sourceSlot === '' ||
      sourceSlot === null;

    this.dataset.overflowExpanded =
      String(!isDefaultSlot);

    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      const previousSubmenu =
        findSubmenu(previouslyActiveItem);

      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              /*
               * Ignore observer callbacks for an item that is no
               * longer active.
               */
              if (this.#state.activeItem !== item) {
                return;
              }

              const currentHeight =
                submenu?.offsetHeight ?? 0;

              if (currentHeight <= 0) return;

              headerComponent.style.setProperty(
                '--submenu-height',
                `${currentHeight}px`
              );

              this.#setFullOpenHeaderHeight(
                currentHeight
              );

              this.#cleanupMutationObserver();
            });
          });
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      clearTimeout(this.#observerCleanupTimer);

      this.#observerCleanupTimer = setTimeout(() => {
        this.#cleanupMutationObserver();
        this.#observerCleanupTimer = undefined;
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight ?? 0;

    /*
     * For overflow menus, use the greater of the overflow submenu
     * height and the total overflow-list link height.
     */
    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight ?? 0;

        finalHeight = Math.max(
          overflowHeight,
          overflowListHeight
        );
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(finalHeight);

    this.style.setProperty(
      '--submenu-opacity',
      finalHeight > 0 ? '1' : '0'
    );

    this.#startPointerTracking(
      item,
      previouslyActiveItem
    );
  }

  /**
   * Deactivate unless focus or the pointer is moving into the submenu.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const submenu = findSubmenu(activeItem);

    const relatedTarget =
      event.relatedTarget instanceof Node
        ? event.relatedTarget
        : null;

    const isMovingToSubmenu =
      relatedTarget !== null &&
      Boolean(submenu?.contains(relatedTarget));

    const isFocusStillWithinSubmenu =
      document.activeElement instanceof Node &&
      Boolean(
        submenu?.contains(document.activeElement)
      );

    const isMovingToActiveItem =
      relatedTarget !== null &&
      activeItem.contains(relatedTarget);

    const isMovingToOverflowItem =
      relatedTarget instanceof Element &&
      Boolean(
        relatedTarget.closest('[slot="overflow"]')
      );

    const overflowMenu = this.overflowMenu;

    const isMovingToOverflowMenu =
      relatedTarget instanceof Element &&
      Boolean(
        overflowMenu &&
          (relatedTarget === overflowMenu ||
            overflowMenu.contains(relatedTarget))
      );

    if (
      isMovingToSubmenu ||
      isFocusStillWithinSubmenu ||
      isMovingToActiveItem ||
      isMovingToOverflowItem ||
      isMovingToOverflowMenu
    ) {
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately.
   *
   * @param {HTMLElement | null} item
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    const headerComponent = this.headerComponent;

    headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(0);

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem = null;

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }

    clearTimeout(this.#observerCleanupTimer);
    this.#observerCleanupTimer = undefined;

    this.#cleanupMutationObserver();
  };

  /**
   * Calculate the height of the links in the overflow menu.
   *
   * @returns {number}
   */
  #getOverflowListLinksHeight() {
    const slot =
      this.overflowMenu?.querySelector('slot');

    const slottedMenuLinks =
      slot instanceof HTMLSlotElement
        ? slot.assignedElements()
        : null;

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight ?? 0;
    }

    /**
     * Run a callback for each overflow submenu.
     *
     * @param {Function} callback
     */
    const mapSubmenus = (callback) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = link.querySelector(
          '[ref="submenu[]"]'
        );

        if (submenu instanceof HTMLElement) {
          callback(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height =
      this.overflowMenu?.offsetHeight ?? 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  /**
   * Set the height of the fully opened header.
   *
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    const headerComponent = this.headerComponent;

    if (!headerComponent) return;

    const isOverlapSituation =
      headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const topRow = headerComponent.querySelector(
      '.header__row--top'
    );

    let headerVisibleHeight =
      headerComponent.offsetHeight;

    if (
      isOverlapSituation &&
      headerComponent.offsetHeight > 0
    ) {
      headerVisibleHeight =
        topRow instanceof HTMLElement
          ? topRow.offsetHeight
          : 0;
    }

    const fullOpenHeaderHeight =
      submenuHeight === 0
        ? 0
        : submenuHeight + headerVisibleHeight;

    headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images used inside the header menu.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  /**
   * Disconnect the submenu mutation observer.
   */
  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find the menu item associated with an element.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  /*
   * Handle the visible "More" item by selecting the first item
   * in its overflow slot.
   */
  const moreItem = element.closest('[slot="more"]');

  if (moreItem) {
    const overflowItem =
      moreItem.parentElement?.querySelector(
        '[slot="overflow"]'
      );

    if (
      overflowItem &&
      overflowItem !== moreItem
    ) {
      return findMenuItem(overflowItem);
    }
  }

  if (
    element instanceof HTMLElement &&
    element.matches('[ref="menuitem"]')
  ) {
    return element;
  }

  const closestMenuItem = element.closest(
    '[ref="menuitem"]'
  );

  if (closestMenuItem instanceof HTMLElement) {
    return closestMenuItem;
  }

  const listItem = element.closest(
    '.menu-list__list-item'
  );

  const nestedMenuItem =
    listItem?.querySelector('[ref="menuitem"]') ??
    element.querySelector('[ref="menuitem"]');

  return nestedMenuItem instanceof HTMLElement
    ? nestedMenuItem
    : null;
}

/**
 * Find the submenu associated with a menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}